import { ErrorState } from "@/components/ui/error-state";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { StatusBadge } from "@/components/ui/status-badge";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, BarChart3, Cpu, Download } from "lucide-react";
import { useDownloadDataset } from "./mutations";
import { useDataset, useDatasetSample } from "./queries";

export function DatasetDetailPage() {
  const { datasetId } = useParams({ from: "/datasets/$datasetId" });
  const { data: dataset, isLoading, isError, refetch } = useDataset(datasetId);
  const { data: sample, isLoading: sampleLoading } = useDatasetSample(datasetId);
  const download = useDownloadDataset();
  const navigate = useNavigate();

  if (isLoading)
    return (
      <div className="pt-2">
        <TableSkeleton rows={3} cols={4} />
      </div>
    );
  if (isError || !dataset)
    return (
      <div className="pt-2">
        <ErrorState message="Dataset not found" onRetry={() => refetch()} />
      </div>
    );

  return (
    <>
      <PageHeaderV2
        eyebrow="Data"
        title={dataset.name}
        meta={
          <span className="flex items-center gap-3">
            <StatusBadge status={dataset.status} />
            <span>{dataset.kind}</span>
            {dataset.row_count != null && <span>{dataset.row_count.toLocaleString()} rows</span>}
            {dataset.column_count != null && <span>{dataset.column_count} cols</span>}
          </span>
        }
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate({ to: "/datasets" })}
              className="rounded-md border border-line px-3 py-2 text-xs font-medium text-muted hover:text-ink"
            >
              <ArrowLeft size={14} className="mr-1 inline" />
              Back
            </button>
            {dataset.status === "ready" && (
              <>
                <button
                  type="button"
                  onClick={() => navigate({ to: `/datasets/${datasetId}/eda` })}
                  className="flex items-center gap-1.5 rounded-[10px] border border-line px-3 py-2 text-xs font-semibold text-ink hover:bg-code-bg"
                >
                  <BarChart3 size={14} />
                  Run EDA
                </button>
                <button
                  type="button"
                  onClick={() => navigate({ to: "/train", search: { dataset: datasetId } })}
                  className="flex items-center gap-1.5 rounded-[10px] border border-line px-3 py-2 text-xs font-semibold text-ink hover:bg-code-bg"
                >
                  <Cpu size={14} />
                  Train
                </button>
                <button
                  type="button"
                  onClick={() => download.mutate(datasetId)}
                  disabled={download.isPending}
                  className="flex items-center gap-1.5 rounded-[10px] bg-navy-700 px-3 py-2 text-xs font-semibold text-white shadow-sm dark:bg-teal-500 dark:text-navy-900"
                >
                  <Download size={14} />
                  {download.isPending ? "…" : "Download"}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Schema table */}
      <div className="mb-6">
        <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
          Schema
        </h2>
        {dataset.schema && dataset.schema.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-line bg-code-bg">
                  <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Column
                  </th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Nullable
                  </th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Distinct
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataset.schema.map((col) => (
                  <tr key={col.name} className="border-b border-line last:border-b-0">
                    <td className="px-3 py-2 font-mono font-medium">{col.name}</td>
                    <td className="px-3 py-2 font-mono text-muted">{col.dtype}</td>
                    <td className="px-3 py-2 font-mono text-muted">
                      {col.nullable ? "yes" : "no"}
                    </td>
                    <td className="px-3 py-2 font-mono text-muted tabular-nums">
                      {col.distinct ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted">Schema not yet available.</p>
        )}
      </div>

      {/* Sample */}
      <div className="mb-7">
        <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
          Sample rows
        </h2>
        {sampleLoading ? (
          <TableSkeleton rows={3} cols={5} />
        ) : sample && sample.rows.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-line bg-code-bg">
                  {sample.columns.map((c) => (
                    <th
                      key={c.name}
                      className="whitespace-nowrap px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300"
                    >
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sample.rows.map((row, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: sample rows are display-only and lack a stable identity
                  <tr key={i} className="border-b border-line last:border-b-0">
                    {sample.columns.map((c) => (
                      <td
                        key={c.name}
                        className="max-w-[200px] truncate whitespace-nowrap px-3 py-2 font-mono text-muted tabular-nums"
                      >
                        {String(row[c.name] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {sample.truncated && (
              <div className="border-t border-line px-3 py-2 text-center font-mono text-[10px] text-muted">
                Showing {sample.rows.length} of {sample.row_count} rows
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted">
            No sample available — dataset may still be processing.
          </p>
        )}
      </div>
    </>
  );
}
